import { withoutDrawnEdgeData } from './edge-deserialization'
import { reconcileStaticMode } from './static-mode'
import { withCustomName } from './node-display-name'
import type { NodeInput } from '@/features/backtest/types/nodes'
import {
  NODES_WITH_INPUTS_OUTPUTS,
  DYNAMIC_INPUT_NODES,
} from '@/features/backtest/constants/node-type-groups'
import { isPassthroughType } from '@/features/backtest/utils/inline-passthroughs'
import { UniversalNodeRegistry } from '@/features/backtest/schemas/universal-node-schema'

/**
 * Resolve an edge's effective source through group I/O pass-throughs: the
 * serialized `input.source` must name the REAL upstream output ('close',
 * 'ema', …), never a pass-through's 'out' handle — the engine inlines the
 * pass-through nodes but reads `input.source` as-is.
 */
const resolveEdgeSource = (
  edge: Record<string, unknown>,
  nodes: Array<Record<string, unknown>>,
  edges: Array<Record<string, unknown>>,
): { sourceNode: Record<string, unknown> | undefined; sourceHandle: string | undefined } => {
  let sourceNode = nodes.find((n) => n.id === edge.source)
  let sourceHandle = edge.sourceHandle as string | undefined
  const seen = new Set<unknown>()
  while (sourceNode && isPassthroughType(sourceNode.type as string) && !seen.has(sourceNode.id)) {
    seen.add(sourceNode.id)
    const currentId = sourceNode.id
    const upstream = edges.find((e) => e.target === currentId)
    if (!upstream) return { sourceNode: undefined, sourceHandle: undefined }
    sourceHandle = upstream.sourceHandle as string | undefined
    sourceNode = nodes.find((n) => n.id === upstream.source)
  }
  return { sourceNode, sourceHandle }
}

const isNumericKey = (k: string) => /^\d+$/.test(k)

const OHLCV_FIELDS = new Set(['open', 'high', 'low', 'close', 'volume'])

/**
 * Form-builder advisory tags that MUST survive DB save/reload/undo.
 *
 * The form builder stamps a single `fb` tag onto `node.data` (see
 * form-builder/fb-tags.ts) so derive-form can round-trip form-authored
 * structure. The cleaner otherwise whitelists `node.data` down to a fixed set
 * of engine fields, which would silently drop the tag — degrading reloaded
 * form sections to the locked "Edit in builder" fallback. Preserve ONLY these
 * named keys; the allowlist stays closed to everything else. The backend
 * treats `node.data` as an opaque JSON blob (the trim is payload hygiene, not
 * a schema constraint), so carrying the tag through is safe.
 */
export const PERSISTED_FORM_TAG_KEYS = ['fb'] as const

/** Extract just the whitelisted form-builder tags present on a node's data. */
const pickFormTags = (data: Record<string, unknown>): Record<string, unknown> => {
  const out: Record<string, unknown> = {}
  for (const key of PERSISTED_FORM_TAG_KEYS) {
    if (data[key] !== undefined) out[key] = data[key]
  }
  return out
}

/**
 * Extract a debug node's hand-picked series colours, when it has any.
 *
 * Returns `{}` — not `{ debugColors: undefined }` — for a node with no colours
 * or an empty map, so the serialized payload of the overwhelming majority of
 * nodes is byte-identical to what it was before this field existed.
 */
const pickDebugColors = (data: Record<string, unknown>): Record<string, unknown> => {
  const colors = data.debugColors
  if (!colors || typeof colors !== 'object' || Array.isArray(colors)) return {}
  return Object.keys(colors).length > 0 ? { debugColors: colors } : {}
}

/**
 * Backfill any schema-default parameters a node's serialized list is missing.
 *
 * Load-bearing defaults — chiefly priceData's `extendedHours` (schema default
 * `true`, "full session unless opted out") — were silently dropped from saved
 * specs whenever the node never carried the param, so the backtest data loader
 * fell back to RTH and indicators "didn't build in extended hours". Filling the
 * missing defaults here makes the serialized spec reflect the schema. Params
 * already present — including an explicit opt-out like `extendedHours: false` —
 * are left untouched (only absent ones are added).
 */
export const withSchemaDefaults = (nodeType: string, params: unknown): unknown => {
  if (!Array.isArray(params)) return params
  const schema = UniversalNodeRegistry.getSchemaResolved(nodeType)
  if (!schema) return params
  let filled = [...(params as Array<Record<string, unknown>>)]
  if (nodeType === 'entryRules') {
    const definitions = new Map(
      schema.parameters.map((definition) => [definition.name, definition]),
    )
    const values: Record<string, unknown> = Object.fromEntries(
      schema.parameters.map((definition) => [definition.name, definition.default]),
    )
    for (const parameter of filled) {
      if (typeof parameter.name === 'string') values[parameter.name] = parameter.value
    }
    const isVisible = (definition: (typeof schema.parameters)[number]) =>
      (!definition.dependsOn ||
        definition.dependsOn.values.includes(
          values[definition.dependsOn.parameter] as string | number | boolean,
        )) &&
      (definition.dependsOnAll ?? []).every((dependency) =>
        dependency.values.includes(values[dependency.parameter] as string | number | boolean),
      )
    filled = filled.filter((parameter) => {
      if (typeof parameter.name !== 'string') return true
      const definition = definitions.get(parameter.name)
      return !definition || isVisible(definition)
    })
    const present = new Set(filled.map((p) => p?.name))
    for (const definition of schema.parameters) {
      if (
        !present.has(definition.name) &&
        definition.default !== undefined &&
        isVisible(definition)
      ) {
        filled.push({ name: definition.name, value: definition.default })
      }
    }
    return filled
  }
  const present = new Set(filled.map((p) => p?.name))
  for (const def of schema.parameters) {
    if (!present.has(def.name) && def.default !== undefined) {
      filled.push({ name: def.name, value: def.default })
    }
  }
  return filled
}

/**
 * Coerces a node's `parameters` into the descriptor-LIST form the backend
 * (`XYFlowModel`) requires (`parameters: List[Parameter]`). Handles three inputs:
 *
 *  - **already a list** → passed through unchanged.
 *  - **corrupted hybrid dict** `{ '0': {name,value,…}, '1': {…}, targetDate: 'x' }`
 *    — produced by an older `{ ...arrayParams, [key]: value }` edit that spread an
 *    array into an object literal. The numeric keys hold the original descriptors;
 *    any non-numeric key is an edited override applied back onto the matching
 *    descriptor by name. This heals data that was already saved in the bad shape.
 *  - **plain record** `{ name: value }` → mapped to `[{ name, value }]`.
 */
export const normalizeParametersToList = (parameters: unknown): unknown => {
  if (parameters == null || Array.isArray(parameters) || typeof parameters !== 'object') {
    return parameters
  }
  const entries = Object.entries(parameters as Record<string, unknown>)
  const descriptors = entries
    .filter(
      ([k, v]) => isNumericKey(k) && v != null && typeof v === 'object' && 'name' in (v as object),
    )
    .sort(([a], [b]) => Number(a) - Number(b))
    .map(([, v]) => ({ ...(v as Record<string, unknown>) }))

  if (descriptors.length === 0) {
    // Pure record form: { period: 72 } → [{ name: 'period', value: 72 }]
    return entries.map(([name, value]) => ({ name, value }))
  }

  // Apply edited overrides (non-numeric keys) onto the reconstructed descriptors.
  for (const [name, value] of entries) {
    if (isNumericKey(name)) continue
    const hit = descriptors.find((d) => d.name === name)
    if (hit) hit.value = value
    else descriptors.push({ name, value })
  }
  return descriptors
}

/**
 * Resolve the `source` field an input should carry, from the upstream node type
 * and the edge's source handle. OHLCV connections default to `close`; every
 * other handle (indicator/math output id like `sma`, `ema`, `result`) passes
 * through verbatim. Mirrors the live `onConnect` resolution.
 */
const getSourceOutputName = (sourceNodeType: string, sourceHandle: string | undefined): string => {
  if (sourceHandle === 'ohlcv' || sourceHandle === 'close') return 'close'
  if (sourceHandle === 'open') return 'open'
  if (sourceHandle === 'high') return 'high'
  if (sourceHandle === 'low') return 'low'
  if (sourceHandle === 'volume') return 'volume'
  if (sourceHandle === 'mid') return 'mid'
  if (sourceHandle === 'ema') return 'ema'
  if (sourceHandle === 'sma') return 'sma'
  if (sourceHandle === 'rsi') return 'rsi'
  if (sourceHandle === 'value') return 'value'
  if (sourceHandle === 'result') return 'result'
  return sourceHandle || sourceNodeType
}

/**
 * Heal a node's `inputs` so every wired node-type input carries the `source`
 * its incoming edge says: the price field for a bar, else the output line.
 *
 * Why this exists: `input.source` is denormalized edge state — only the manual
 * `onConnect` path keeps it in sync. Every other connection path (auto-connect
 * on drop, wire insertion, paste, bypass-on-delete, edge reconnect) creates a
 * valid edge but leaves `source` null. Codegen then rejects the spec
 * (`barOffset … must have a valid NODE input with source`). The edge always
 * carries the upstream handle, so we backfill from it at serialization — the
 * single chokepoint every export/save/run passes through.
 *
 * Only fills a missing source: an explicit field choice (e.g. a priceData input
 * set to `high`) is preserved.
 *
 * OHLCV inputs get one extra correction. An ohlcv edge's `sourceHandle` is the
 * output id (`ohlcv`), which does NOT encode the field — so for an OHLCV upstream
 * the engine reads the field from `input.source`. Cookbook / AI-materialized
 * graphs (graph-engine/materialize.ts) instead store the upstream STEP id there
 * (e.g. `price`) with the real field in a separate `field` prop. That step id
 * then lands in the engine's OHLCV field slot → "Invalid OHLCV value: 'price'".
 * When the resolved upstream is priceData/marketData and `source` isn't already a
 * valid field, we rewrite it to the materialized `field` (default `close`).
 */
export function backfillInputSourcesFromEdges(
  inputs: unknown,
  nodeId: string,
  nodes: Array<Record<string, unknown>>,
  edges: Array<Record<string, unknown>>,
): unknown {
  if (!Array.isArray(inputs)) return inputs
  return inputs.map((input) => {
    const inp = (input ?? {}) as Record<string, unknown>
    if (inp.inputType !== 'node') return input
    const edge = edges.find((e) => e.target === nodeId && e.targetHandle === inp.name)
    if (!edge) return input // no backing edge — nothing to derive from
    const { sourceNode, sourceHandle } = resolveEdgeSource(edge, nodes, edges)
    if (!sourceNode) return input
    const sourceType = ((sourceNode.data as Record<string, unknown> | undefined)?.type ||
      sourceNode.type ||
      '') as string

    if (sourceType === 'priceData' || sourceType === 'marketData') {
      // OHLCV upstream: canonical `field` wins over the legacy `source`
      // selection. The backend still consumes `source`, so keep it synchronized.
      const field =
        typeof inp.field === 'string' && OHLCV_FIELDS.has(inp.field)
          ? inp.field
          : typeof inp.source === 'string' && OHLCV_FIELDS.has(inp.source)
            ? inp.source
            : 'close'
      return inp.source === field ? input : { ...inp, source: field }
    }

    // Non-OHLCV: the wire is the truth (connection audit step 7, 2026-10-06).
    // The engine reads a wired input's source before its edge's handle, so a
    // source left from an earlier wire read the wrong line; the stored value
    // (the editor's live mirror) is advisory. An unwired input keeps whatever
    // it holds: the engine pairs inputs with edges and ignores it. A wire with
    // no handle keeps a stored source: the engine then reads the source's one
    // output, where the fallback below would name the node type.
    if (!sourceHandle && inp.source != null && inp.source !== '') return input
    const derived = getSourceOutputName(sourceType, sourceHandle)
    return inp.source === derived ? input : { ...inp, source: derived }
  })
}

/**
 * The parts of a node's saved data that come from the graph around it (or need
 * list-normalizing) rather than from the node's own fields. The save cleaner
 * derives them from the edges; the clipboard passes the live values as they are.
 */
export interface NodeDataDerived {
  parameters: unknown
  /** Only read for nodes with an inputs/outputs structure. */
  inputs?: unknown
  /** Only read for subnets. */
  proxyEdgePoints?: unknown
}

/**
 * The ONE per-kind allowlist of node data, shared by save and the clipboard so
 * a field added here round-trips through both (the clipboard once dropped `fb`,
 * `presetId` and `compact`, which left a pasted Form subnet unreadable).
 * Closed on purpose: nothing outside these lists is carried.
 */
export function pickNodeData(
  nodeType: string,
  nodeData: Record<string, unknown>,
  derived: NodeDataDerived,
): Record<string, unknown> {
  const isPreset = nodeType.includes(':')
  const baseType = isPreset ? nodeType.split(':')[0] : nodeType
  // Read presetId from data (new format) with fallback to legacy 'preset' field
  const presetId = isPreset ? nodeType.split(':')[1] : nodeData.presetId || nodeData.preset

  if (nodeType === 'group') {
    // Annotation sheet: label, note text, color, and font sizes
    return {
      label: nodeData.label,
      note: nodeData.note,
      color: nodeData.color,
      titleFontSize: nodeData.titleFontSize,
      noteFontSize: nodeData.noteFontSize,
    }
  }
  if (nodeType === 'sticky') {
    // Sticky note: note text, color, and its font size
    return {
      note: nodeData.note,
      color: nodeData.color,
      noteFontSize: nodeData.noteFontSize,
    }
  }
  if (nodeType === 'subnet') {
    return {
      label: nodeData.label,
      fontSize: nodeData.fontSize,
      titleEnabled: nodeData.titleEnabled,
      color: nodeData.color,
      type: nodeData.type,
      category: nodeData.category,
      parameters: derived.parameters,
      collapsed: nodeData.collapsed,
      expandedSize: nodeData.expandedSize,
      // What an expand pushed, the groups it grew and where the card sat: a reload while
      // expanded still collapses exactly (utils/make-room.ts, utils/subnet-fit.ts). A
      // collapsed subnet carries none of them.
      madeRoom: nodeData.madeRoom,
      grewGroups: nodeData.grewGroups,
      cardAnchor: nodeData.cardAnchor,
      // A template's subnet, not yet organized (subnet-auto-layout.ts).
      autoLayout: nodeData.autoLayout,
      exposure: nodeData.exposure,
      proxyEdgePoints: derived.proxyEdgePoints,
    }
  }
  const shared = {
    type: nodeData.type,
    label: nodeData.label,
    category: nodeData.category,
    parameters: derived.parameters,
  }
  const tail = {
    presetId: presetId, // Store preset ID for preset nodes
    compact: nodeData.compact, // collapsed + locked UI state
    // Hand-picked debug series colours. Cosmetic but PERSISTED (unlike
    // debugValueSnapshot / debugValueRulerKeys, which are ephemeral): a colour
    // the user chose must survive a reload. Omitted entirely when empty so
    // untouched nodes don't grow a dead key.
    ...pickDebugColors(nodeData),
    // Form-builder tags: must round-trip so reloaded sections stay
    // form-editable (see PERSISTED_FORM_TAG_KEYS).
    ...pickFormTags(nodeData),
    // The user's name for the node (utils/node-display-name); no key when unnamed.
    ...withCustomName({}, nodeData.customName),
  }
  if (NODES_WITH_INPUTS_OUTPUTS.has(baseType)) {
    return { ...shared, inputs: derived.inputs, outputs: nodeData.outputs, ...tail }
  }
  return { ...shared, ...tail }
}

/** What the save cleaner derives from the edges for one node (see NodeDataDerived). */
function deriveSaveFields(
  node: Record<string, unknown>,
  nodeType: string,
  nodeData: Record<string, unknown>,
  nodes: Array<Record<string, unknown>>,
  edges: Array<Record<string, unknown>>,
  proxyPointsByGroup: Map<string, Record<string, unknown>>,
): NodeDataDerived {
  const baseType = nodeType.includes(':') ? nodeType.split(':')[0] : nodeType
  const parameters = normalizeParametersToList(nodeData.parameters)
  if (nodeType === 'subnet') {
    return {
      parameters,
      proxyEdgePoints: proxyPointsByGroup.has(node.id as string)
        ? {
            ...((nodeData.proxyEdgePoints as Record<string, unknown>) ?? {}),
            ...proxyPointsByGroup.get(node.id as string),
          }
        : nodeData.proxyEdgePoints,
    }
  }
  if (nodeType === 'group' || nodeType === 'sticky' || !NODES_WITH_INPUTS_OUTPUTS.has(baseType)) {
    return { parameters }
  }
  // Math and Data nodes - preserve inputs/outputs structure
  // For dynamic input nodes (sum, average, max, min), rebuild inputs from edges
  let inputs = nodeData.inputs

  if (DYNAMIC_INPUT_NODES.has(baseType)) {
    // Find all edges targeting this node
    const incomingEdges = edges.filter((edge) => edge.target === node.id)

    if (incomingEdges.length > 0) {
      // Build inputs array from actual edge connections
      inputs = incomingEdges.map((edge, index) => {
        const { sourceNode, sourceHandle } = resolveEdgeSource(edge, nodes, edges)
        const sourceData = (sourceNode?.data ?? {}) as Record<string, unknown>
        const sourceType = (sourceData.type || sourceNode?.type || '') as string
        const sourceName = getSourceOutputName(sourceType, sourceHandle)

        return {
          name: edge.targetHandle || `input_${index + 1}`,
          source: sourceName,
          inputType: 'node',
        }
      })
    }
  }

  // Input B typed or wired: its setting decides (audit step 7).
  inputs = reconcileStaticMode(baseType, nodeData.parameters, inputs as NodeInput[] | undefined)

  // Heal any node-input left with a null `source` (created via a path other
  // than onConnect: auto-connect, wire-insert, paste, bypass) by deriving it
  // from the incoming edge. Dynamic nodes already rebuilt source above, so this
  // is a no-op for them.
  inputs = backfillInputSourcesFromEdges(inputs, node.id as string, nodes, edges)

  return {
    // These nodes carry load-bearing defaults which must not be dropped when
    // the control still shows its schema value.
    parameters:
      baseType === 'priceData' || baseType === 'entryRules'
        ? withSchemaDefaults(baseType, parameters)
        : parameters,
    inputs,
  }
}

// Unified data cleaning function used by all operations (copySchema, database saving, undo/redo)
export const cleanDataForSerialization = (
  nodes: Array<Record<string, unknown>>,
  edges: Array<Record<string, unknown>>,
) => {
  // Proxy edges are dropped below, but the wire shapes users gave them
  // (control points) persist on the owning group so reload restores them.
  const proxyPointsByGroup = new Map<string, Record<string, unknown>>()
  for (const edge of edges) {
    const edgeData = edge.data as Record<string, unknown> | undefined
    if (edgeData?.proxy !== true) continue
    const points = edgeData.points as unknown[] | undefined
    if (!points || points.length === 0) continue
    const targetIsGroup = nodes.find((n) => n.id === edge.target)?.type === 'subnet'
    const ownerId = (targetIsGroup ? edge.target : edge.source) as string
    const bucket = proxyPointsByGroup.get(ownerId) ?? {}
    bucket[edge.id as string] = points
    proxyPointsByGroup.set(ownerId, bucket)
  }

  const cleanedNodes = nodes.map((node) => {
    const nodeType = node.type as string
    const nodeData = (node.data ?? {}) as Record<string, unknown>
    // Preset nodes have a colon in the type, like "timeSlice:premarket-high"
    const baseType = nodeType.includes(':') ? nodeType.split(':')[0] : nodeType

    return {
      ...node,
      // For preset nodes, use base type instead of compound type
      type: baseType,
      // Clean visual state properties
      selected: false,
      dragging: false,
      highlighted: false,
      active: false,
      // `hidden` is collapse-projection state, never persisted — the backend
      // always receives the full graph regardless of collapsed groups
      hidden: undefined,
      // Preserve parentId for group relationships
      parentId: node.parentId,
      // Preserve style for group/backdrop/sticky nodes (contains width/height dimensions)
      style:
        nodeType === 'subnet' || nodeType === 'group' || nodeType === 'sticky'
          ? node.style
          : undefined,
      // Include explicit dimensions for resizable nodes (groups)
      ...(node.width ? { width: node.width } : {}),
      ...(node.height ? { height: node.height } : {}),
      // Include measured dimensions for groups
      ...(nodeType === 'subnet' && node.measured ? { measured: node.measured } : {}),
      // Clean data properties while preserving essential information
      data: pickNodeData(
        nodeType,
        nodeData,
        deriveSaveFields(node, nodeType, nodeData, nodes, edges, proxyPointsByGroup),
      ),
    }
  })

  const cleanedEdges = edges
    // Proxy edges are a collapse-projection artifact (rerouted to the group's
    // proxy handles); only the canonical edges they shadow are persisted.
    .filter((edge) => (edge.data as Record<string, unknown> | undefined)?.proxy !== true)
    .map((edge) => ({
      id: edge.id,
      source: edge.source,
      target: edge.target,
      sourceHandle: edge.sourceHandle,
      targetHandle: edge.targetHandle,
      type: edge.type,
      // What the canvas only draws (off, dead, highlighted) is never saved.
      data: withoutDrawnEdgeData(edge).data,
      hidden: undefined,
    }))

  return {
    nodes: cleanedNodes,
    edges: cleanedEdges,
  }
}
